/* MACROS — what went in today, the week behind it, and the month behind that.

   Three horizons, two sources, split the way the handoff splits them:

   - Today's tiles and the LAST-7-DAYS rows are raw entries from `/nutrition`.
     There is nothing derived about a day's totals — they are its rows added up.
   - WEEKLY-AVG.4W is `/trends`' `macros_weekly`, computed by
     `src/milo/queries.py`. Averaging four weeks here instead would mean this
     screen and the coach quoting different numbers for the same week, and the
     server's rule ("days with data only") is the subtle part.

   The three reads load independently: a failed `/trends` leaves the seven-day
   log intact, a failed `/targets` leaves both panels correct on the fallback
   axis, and vice versa.

   The kcal axis comes from `/targets` — `queries.current_target`, the same
   resolution `get_coaching_context` and `get_adherence` use — and falls back to
   `KCAL_SCALE` when there is no calorie target to use. It is resolved ONCE per
   render pass and handed down as a value; see `kcalScale` for why nothing here
   may resolve a target per day or per bar.

   One rule is mirrored rather than fetched, and deliberately so — see
   `dayTotals` below. */
import { useEffect, useState } from 'react'
import { Alert, Metric, Pill, Window } from '../ds'
import {
  errorMessage,
  getNutrition,
  getTargets,
  getTrends,
  type MacroField,
  type MacroWeek,
  type NutritionEntry,
  type TargetsResponse,
} from '../api'
import { headingDate } from './format'
import { staleLabel, useShellSync } from '../app/syncOnLoad'
import '../styles/trends.css'

/* ── Constants ───────────────────────────────────────────────────────────── */

/** Rows in LAST-7-DAYS.LOG, today included. */
const DAYS = 7

/** Extra days asked for below the seven-day window, to absorb a browser clock
 *  that disagrees with the server's. See the two reads in `Macros` — the lower
 *  edge of that fetch is the one date this screen derives from `new Date()`,
 *  and nothing about what is *displayed* hangs off it. */
const FETCH_SLACK = 7

/**
 * The kcal axis when the user's own calorie target cannot be used, and the footer
 * says which of those it is on.
 *
 * A fixed scale, not a per-window max: the point of the bars is comparing days to
 * each other and to a ceiling, which a rescaling axis would destroy. That reasoning
 * is unchanged and is why this constant survives — what changed is only that a real
 * target now exists to prefer over it. On a 1,800 kcal cut every bar drawn against
 * 2,200 understates the miss exactly on the days that missed.
 *
 * Exported for `Macros.test.ts`, which pins it as the fallback.
 */
export const KCAL_SCALE = 2200

/* ── Loading ─────────────────────────────────────────────────────────────── */

type Load<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; message: string }

interface Totals {
  calories: number
  protein_g: number
  carbs_g: number
  fat_g: number
}

const NOTHING: Totals = { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 }

interface Nutrition {
  /** ISO date → that day's totals. Only days that have rows appear, and no date
   *  later than the server's today can be among them — see the reads below. */
  byDate: Map<string, Totals>
  /**
   * The server's today, when the two reads name it between them; null when they
   * cannot, which is exactly the case where nothing is logged on that day.
   *
   * Null is not "assume the browser is right". It is the reason the tiles read
   * zero rather than borrowing a neighbouring day's food: this screen promises
   * totals for the server's day, and when it does not know which day that is,
   * the honest total is none.
   */
  todayIso: string | null
  /**
   * The date LAST-7-DAYS.LOG ends on — `todayIso` when it is known, this
   * browser's date when it is not. Labels only: it decides which of the seven
   * rows reads TODAY and which bar is pink, never what any of them contains.
   */
  anchorIso: string
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

/* ── Totals ──────────────────────────────────────────────────────────────── */

/**
 * One day's macros from its rows.
 *
 * A `daily_total` row REPLACES the individual meals for that date; it does not
 * add to them. That is the server's rule, in `macro_weekly_averages`
 * (`src/milo/queries.py`) — a day logged both ways is one day's food entered
 * twice, at two levels of detail, and summing them would double it.
 *
 * This is the one piece of server logic reproduced on the client, and it is
 * reproduced because the alternative is worse: WEEKLY-AVG.4W applies the rule
 * (it comes from the server), so a LAST-7-DAYS row that did not would show a
 * day at 3,400 kcal directly above a weekly average built from 1,700. Two
 * panels, one day, two answers. If the rule ever changes, it changes in both
 * places — hence the citation.
 *
 * Summed raw and rounded once, at the end. `Nutrition.calories` is `Numeric(7,1)`
 * and the wire carries the float, so two 100.4 kcal meals are 200.8 — 201, not
 * the 200 that rounding each row before adding it would give. The display is
 * whole numbers; the arithmetic behind them is not.
 *
 * Exported for `Macros.test.ts`, which pins both rules.
 */
export function dayTotals(rows: readonly NutritionEntry[]): Totals {
  const total = rows.find((row) => row.meal === 'daily_total')
  const counted = total ? [total] : rows
  const sum = counted.reduce<Totals>(
    (acc, row) => ({
      calories: acc.calories + (row.calories ?? 0),
      protein_g: acc.protein_g + (row.protein_g ?? 0),
      carbs_g: acc.carbs_g + (row.carbs_g ?? 0),
      fat_g: acc.fat_g + (row.fat_g ?? 0),
    }),
    NOTHING,
  )
  return {
    calories: Math.round(sum.calories),
    protein_g: Math.round(sum.protein_g),
    carbs_g: Math.round(sum.carbs_g),
    fat_g: Math.round(sum.fat_g),
  }
}

/** Group a flat entry list into per-day totals. */
export function groupByDate(entries: readonly NutritionEntry[]): Map<string, Totals> {
  const rows = new Map<string, NutritionEntry[]>()
  for (const entry of entries) {
    const day = rows.get(entry.date)
    if (day) day.push(entry)
    else rows.set(entry.date, [entry])
  }
  return new Map([...rows].map(([date, day]) => [date, dayTotals(day)]))
}

/* ── Dates ───────────────────────────────────────────────────────────────── */

/** Local midnight on an ISO calendar day — never `new Date(iso)`, which reads a
 *  bare date as UTC and lands a day early west of Greenwich. */
function parseIso(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function toIso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/** `count` days before `date`, as a new Date. Calendar arithmetic, so it steps
 *  over a DST boundary and a month end without either being a special case. */
function daysBefore(date: Date, count: number): Date {
  const out = new Date(date.getTime())
  out.setDate(out.getDate() - count)
  return out
}

/**
 * The server's today, named by the data rather than assumed from this clock —
 * or null when the data cannot name it.
 *
 * The two reads in `Macros` are the two halves of an inequality, both drawn by
 * `resolve_range` in `src/milo/queries.py`:
 *
 *   - `days: 0`   → `since = config.today()`, no upper bound: `date >= today`.
 *   - `from`, no `to` → `until = config.today()`:            `date <= today`.
 *
 * A date that appears in both is therefore a date that is both on-or-after and
 * on-or-before the server's today, which is to say it *is* the server's today.
 * One row anywhere on that day is enough to pin it, and the browser's clock does
 * not enter into it.
 *
 * Null means no row is dated the server's today — the ordinary "nothing logged
 * yet" morning, and also the case where the only rows at or after today are
 * dated in the FUTURE. Taking the first row of the `days: 0` read instead, as
 * this screen once did, folded those future rows into the tiles and moved the
 * seven-day window's anchor onto their date.
 *
 * Exported for `Macros.test.ts`.
 */
export function serverToday(
  atOrAfterToday: readonly NutritionEntry[],
  throughToday: ReadonlyMap<string, unknown>,
): string | null {
  for (const entry of atOrAfterToday) {
    if (throughToday.has(entry.date)) return entry.date
  }
  return null
}

/** "05 WED" — the day-column label. */
function dayLabel(date: Date): string {
  return date
    .toLocaleDateString('en-US', { weekday: 'short', day: '2-digit' })
    .replace(',', '')
    .toUpperCase()
}

/** "AUG 03" — the week-column label. */
function monthLabel(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' }).replace(',', '').toUpperCase()
}

/* ── The axis ────────────────────────────────────────────────────────────── */

/**
 * Why the kcal bars are drawn against the number they are drawn against.
 *
 * Four states rather than "target or not", because the footer states this out loud
 * and three of the four are honest in different ways. `unknown` (the read has not
 * landed, or failed) must never be reported as `none`: the app does not know that
 * the user has no target, and saying so would be a claim it cannot support.
 * `unusable` is a stored calorie target of 0 — `TargetIn` allows `ge=0`, so it is
 * reachable — which is a real target and not an axis.
 */
export type ScaleSource = 'target' | 'none' | 'unknown' | 'unusable'

export interface KcalScale {
  /** Strictly positive, always. Every division on this screen goes through it. */
  kcal: number
  source: ScaleSource
}

/**
 * The kcal axis, resolved ONCE per render pass from the targets read.
 *
 * Takes the whole response rather than a date, and returns a number rather than a
 * lookup, so that nothing downstream can resolve a per-day target: the seven-day
 * window spans days whose targets may differ, and `current_target(as_of=<that day>)`
 * would answer each of them correctly — but a bar drawn against its own day's target
 * makes two days at 1,900 kcal draw at different widths, which destroys the one
 * comparison this panel exists for. Per-day targets belong in a per-day view; the
 * axis is today's.
 *
 * Exported for `Macros.test.ts`.
 */
export function kcalScale(loaded: TargetsResponse | null): KcalScale {
  if (loaded === null) return { kcal: KCAL_SCALE, source: 'unknown' }
  const calories = loaded.targets?.calories ?? null
  if (calories === null) return { kcal: KCAL_SCALE, source: 'none' }
  if (!Number.isFinite(calories) || calories <= 0) {
    return { kcal: KCAL_SCALE, source: 'unusable' }
  }
  return { kcal: calories, source: 'target' }
}

/** "1,800" — the one place a kcal figure gets its separator. */
function kcal(value: number): string {
  return Math.round(value).toLocaleString('en-US')
}

/** The footer line under LAST-7-DAYS.LOG. Says which number the track is, and why. */
export function scaleFooter(scale: KcalScale): string {
  const why =
    scale.source === 'target'
      ? 'YOUR CALORIE TARGET'
      : scale.source === 'none'
        ? 'NO CALORIE TARGET SET'
        : scale.source === 'unusable'
          ? 'CALORIE TARGET OF 0 — NOT AN AXIS'
          : 'TARGET UNREAD'
  const over = scale.source === 'target' ? ' ✦ OVER TARGET HATCHED' : ''
  return `KCAL BAR SCALED TO ${kcal(scale.kcal)} (${why})${over} ✦ P/C/F IN GRAMS`
}

/* ── Bars ────────────────────────────────────────────────────────────────── */

/** Fill width as a percentage of the track, clamped at full. */
export function barWidth(calories: number, scale: KcalScale): string {
  return `${Math.min(100, Math.round((calories / scale.kcal) * 100))}%`
}

/**
 * Did this day go past the calorie CEILING? False whenever the axis is a fallback.
 *
 * Calories are a ceiling (`queries.MACRO_DIRECTION`), so a full bar is not an
 * achievement and past-full is a miss — the opposite reading from a floor. Marking a
 * day "over" the 2,200 fallback would invent a target nobody set, so this is false
 * for every source but `target`. The boundary belongs to both directions: at exactly
 * the target is a hit, not a miss.
 *
 * Exported for `Macros.test.ts`.
 */
export function overTarget(calories: number, scale: KcalScale): boolean {
  return scale.source === 'target' && calories > scale.kcal
}

/* ── Tile chips ──────────────────────────────────────────────────────────── */

export interface MacroChip {
  text: string
  /** `down` is the Metric's red arrow, and is reserved for an actual miss. */
  tone: 'up' | 'down'
}

/**
 * A macro at the precision this screen DISPLAYS it: whole units.
 *
 * Both figures a tile shows are already rounded this way — the value by `dayTotals`,
 * the chip by `kcal` — so this is the granularity at which two consumed values are
 * the same number as far as anyone looking at the screen is concerned. See
 * `macroChip`, the only caller, for why that and not `===` is the right test.
 */
function shown(value: number): number {
  return Math.round(value)
}

/**
 * The delta chip on a macro tile: how much of that macro's target is left today.
 *
 * `remaining` comes from the server (`queries.targets_snapshot`), which is the same
 * block `get_coaching_context` hands the coach — so the number on the tile and the
 * number in the conversation are one number, not two computations of it.
 *
 * Direction comes over the wire too, and decides the wording as well as the colour:
 * 150 kcal past a ceiling is `150 OVER` in red, while 20g short of a protein floor is
 * `20G TO GO` in black and reaching that floor is `TARGET MET`. Rendering the two the
 * same way is the specific wrong picture — going past a floor is good news.
 *
 * `consumed` is the number the TILE is showing, and the chip does not speak unless
 * the targets snapshot agrees with it. `/nutrition` and `/targets` are two reads of
 * two database snapshots, fired in parallel and answered independently; food logged
 * between them — the coach's own `log_food`, a FatSecret sync — lands in one and not
 * the other. `remaining` is `target - consumed` computed against the targets
 * snapshot, so pairing it with a total out of the other snapshot puts two numbers
 * about two different moments side by side: 1,200 on the tile and `150 OVER` beside
 * it, each true of a different second, neither true together. Gated per MACRO rather
 * than per response, because the four tiles are four independent claims — one stale
 * macro must not silence three honest chips. (CodeRabbit, PR #64.)
 *
 * The agreement is tested at DISPLAYED precision, not exactly. The server rounds
 * `consumed` to 0.1 (`remaining_block`) after summing `Numeric` columns in Python;
 * the client sums the floats off the wire and rounds to whole (`dayTotals`). `===`
 * across two routes like that would suppress chips over arithmetic noise that neither
 * number even shows — a worse bug than the one being guarded against — so both sides
 * go through `shown` first. A difference below a whole unit cannot move either
 * rendered figure; a difference of one whole unit is food the tile has not been told
 * about, and is caught.
 *
 * Null means "show no chip": before EITHER read lands, when no target exists at all,
 * when this particular macro has no target of its own, and when the two reads
 * disagree. The sparse case is the subtle one — `set_targets` writes per field, so
 * protein can be set while carbs is null, and 180g of carbs against no carb target is
 * not "180 over 0". All four render identically, as silence, and that is right:
 * an absent chip asserts nothing, and "we cannot say" and "there is nothing to say"
 * both want the app to say nothing. Nor can silence here be read as "no target for
 * this macro" — nothing on this screen prints that. The one claim of that shape is
 * the LAST-7-DAYS footer, and it is driven by `targets`, never by `remaining`, so a
 * suppressed chip cannot make it say NO CALORIE TARGET SET.
 *
 * Exported for `Macros.test.ts`.
 */
export function macroChip(
  loaded: TargetsResponse | null,
  field: MacroField,
  consumed: number | null,
  unit = '',
): MacroChip | null {
  if (loaded === null || consumed === null) return null
  const entry = loaded.remaining?.[field]
  if (!entry || entry.target === null || entry.remaining === null) return null
  if (shown(entry.consumed) !== shown(consumed)) return null
  const left = entry.remaining
  if (loaded.direction[field] === 'floor') {
    return left > 0
      ? { text: `${kcal(left)}${unit} TO GO`, tone: 'up' }
      : { text: 'TARGET MET', tone: 'up' }
  }
  return left >= 0
    ? { text: `${kcal(left)}${unit} LEFT`, tone: 'up' }
    : { text: `${kcal(-left)}${unit} OVER`, tone: 'down' }
}

/* ── Screen ──────────────────────────────────────────────────────────────── */

interface DayRow {
  iso: string
  label: string
  totals: Totals | null
  isToday: boolean
}

export function Macros() {
  /* The app-shell FatSecret sync (issue #88). `epoch` re-runs the three reads
     when a sync the shell watched has finished; `sync` feeds at most one pill. */
  const { epoch, sync } = useShellSync()

  const [nutrition, setNutrition] = useState<Load<Nutrition>>({ status: 'loading' })
  const [weekly, setWeekly] = useState<Load<MacroWeek[]>>({ status: 'loading' })
  const [targets, setTargets] = useState<Load<TargetsResponse>>({ status: 'loading' })

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller

    void (async () => {
      try {
        // Two windows, because between them they bracket the server's day.
        //
        // `days: 0` is "since the server's today" — the same call Log's TODAY
        // SO FAR makes — and it is UNBOUNDED ABOVE: `date >= today`, future
        // rows included. On its own it cannot say which of its rows are today's,
        // which is why nothing below is computed from it except the pin.
        //
        // The range form is the other bound. `from` with no `to` ends at
        // `config.today()` (`resolve_range`), so this read is `date <= server
        // today` and a future-dated row cannot appear in it at all. Every
        // number on this screen — tiles and all seven rows — is selected out of
        // this read alone, which is what makes the future unable to reach them.
        //
        // Its `from` is the one date here taken from this browser's clock, and
        // it is deliberately slack: a floor on what is FETCHED, never a day
        // boundary. A clock a week out still returns the whole seven-day window,
        // and which rows are displayed is decided by `serverToday` regardless.
        const from = toIso(daysBefore(new Date(), DAYS - 1 + FETCH_SLACK))
        const [atOrAfterToday, throughToday] = await Promise.all([
          getNutrition({ days: 0 }, signal),
          getNutrition({ from }, signal),
        ])
        const byDate = groupByDate(throughToday.entries)
        const todayIso = serverToday(atOrAfterToday.entries, byDate)
        setNutrition({
          status: 'ready',
          data: {
            byDate,
            todayIso,
            // Unpinnable only when nothing is dated the server's today, and on
            // that day this browser's date and the server's differ at worst by
            // one — enough to mislabel a row, never enough to invent one, since
            // `byDate` holds no date past the server's today either way.
            anchorIso: todayIso ?? toIso(new Date()),
          },
        })
      } catch (error) {
        if (isAbort(error)) return
        setNutrition({ status: 'error', message: errorMessage(error) })
      }
    })()

    void (async () => {
      try {
        // `macros_weekly` is fixed at the last 4 weeks server-side; `days` does
        // not widen it, so nothing is passed here.
        const trends = await getTrends({}, signal)
        setWeekly({ status: 'ready', data: trends.macros_weekly })
      } catch (error) {
        if (isAbort(error)) return
        setWeekly({ status: 'error', message: errorMessage(error) })
      }
    })()

    /* A third independent load, for the same reason the first two are: the axis is
       worth having and no panel depends on it. A failed targets read leaves every
       number on the screen correct — the bars fall back to KCAL_SCALE and the footer
       says the target went unread — so it costs the panels nothing. */
    void (async () => {
      try {
        setTargets({ status: 'ready', data: await getTargets(signal) })
      } catch (error) {
        if (isAbort(error)) return
        setTargets({ status: 'error', message: errorMessage(error) })
      }
    })()

    return () => controller.abort()
    // `epoch` is the shell sync's refresh signal: a bump means a FatSecret run
    // finished after these reads went out, so run them again.
  }, [epoch])

  const ready = nutrition.status === 'ready' ? nutrition.data : null

  /* Resolved ONCE, here, and handed down as a value. See `kcalScale`: a per-bar or
     per-day resolution would rescale the axis between rows and destroy the only
     comparison the seven-day panel makes. */
  const loadedTargets = targets.status === 'ready' ? targets.data : null
  const scale = kcalScale(loadedTargets)

  /* The tiles are one day's group out of the upper-bounded read, and only when
     that day is known to be the server's. Unknown means nothing is dated it —
     zero is the answer, not whatever the row above or below happens to hold. */
  const totals = (ready?.todayIso ? ready.byDate.get(ready.todayIso) : null) ?? NOTHING

  /* Seven calendar days, oldest first — enumerated rather than taken from the
     response, because a day with no food has no rows and still owes the design
     a NO DATA line. */
  const days: DayRow[] = []
  if (ready) {
    const anchor = parseIso(ready.anchorIso)
    for (let back = DAYS - 1; back >= 0; back--) {
      const date = daysBefore(anchor, back)
      const iso = toIso(date)
      days.push({
        iso,
        label: back === 0 ? 'TODAY' : dayLabel(date),
        // The TODAY row and the tiles above it must never disagree, so when the
        // server's day could not be pinned the row says NO DATA with them. It
        // costs nothing when this clock is right — an unpinned day is one with
        // no rows, so the lookup would have missed anyway.
        totals:
          back === 0 && ready.todayIso === null ? null : ready.byDate.get(iso) ?? null,
        isToday: back === 0,
      })
    }
  }

  const tile = (value: string): string => (ready ? value : '—')

  const loadError =
    nutrition.status === 'error'
      ? nutrition.message
      : weekly.status === 'error'
        ? weekly.message
        : targets.status === 'error'
          ? targets.message
          : null

  /* Non-null only when the shell sync left this screen on cached FatSecret
     data with an age to quote. `new Date()` formats that timestamp for
     display; it decides nothing about which day any data belongs to. */
  const stale = staleLabel(sync, new Date())

  /* One chip per tile, built once, each handed the number ITS OWN tile is showing.
     `macroChip` returns null for a macro with no target of its own or one whose two
     reads disagree, and `Metric` hides the chip entirely for an undefined delta.

     `shownTotals` is null until the NUTRITION read lands, which is what the tile
     itself is gated on — so a chip cannot appear beside a value of "—". That was the
     earlier finding (Greptile, PR #64), and it survives here as the degenerate case
     of the later one (CodeRabbit, PR #64): a chip may only speak about a total the
     tile is actually displaying, and there is no such total yet.

     Four chips, not five. `fiber_g` is a target field with no tile of its own, and a
     chip needs a displayed total to agree with before it may say anything — so the
     keys here are `Totals`', deliberately, and adding a fiber tile is what would add
     a fiber chip. */
  const shownTotals = ready ? totals : null
  const chips: Record<keyof Totals, MacroChip | null> = {
    calories: macroChip(loadedTargets, 'calories', shownTotals?.calories ?? null),
    protein_g: macroChip(loadedTargets, 'protein_g', shownTotals?.protein_g ?? null, 'G'),
    carbs_g: macroChip(loadedTargets, 'carbs_g', shownTotals?.carbs_g ?? null, 'G'),
    fat_g: macroChip(loadedTargets, 'fat_g', shownTotals?.fat_g ?? null, 'G'),
  }

  return (
    <>
      <div className="milo-heading-row">
        <h2 className="milo-heading">Today / {headingDate()}</h2>
        {/* Same rule as LOG's pill: paper, a caveat about freshness — the
            cached numbers below are still right about what they say. */}
        {stale ? <Pill color="paper">{stale}</Pill> : null}
      </div>

      {loadError ? (
        <Alert tone="danger" role="alert" title="Can’t read your macros.">
          {loadError}
        </Alert>
      ) : null}

      {/* The chips were `OF 1,700` and `OF 120G` — literals carried over from the
          design mock, which named a target the app had no way to read and which
          disagreed with the 2,200 the bars below were drawn against. They now carry
          the user's real remaining, or nothing at all when there is no target for
          that macro. */}
      <div className="milo-metrics">
        <Metric
          color="yellow"
          label="CALORIES"
          value={tile(String(totals.calories))}
          delta={chips.calories?.text}
          deltaTone={chips.calories?.tone}
        />
        <Metric
          color="mint"
          label="PROTEIN"
          value={tile(`${totals.protein_g}G`)}
          delta={chips.protein_g?.text}
          deltaTone={chips.protein_g?.tone}
        />
        <Metric
          color="white"
          label="CARBS"
          value={tile(`${totals.carbs_g}G`)}
          delta={chips.carbs_g?.text}
          deltaTone={chips.carbs_g?.tone}
        />
        <Metric
          color="pink"
          label="FAT"
          value={tile(`${totals.fat_g}G`)}
          delta={chips.fat_g?.text}
          deltaTone={chips.fat_g?.tone}
        />
      </div>

      <Window title="LAST-7-DAYS.LOG" shadow="m">
        {nutrition.status === 'loading' ? (
          <div className="milo-note milo-note--sm">LOADING…</div>
        ) : nutrition.status === 'error' ? (
          <div className="milo-note milo-note--sm">SEVEN-DAY LOG UNAVAILABLE</div>
        ) : (
          /* No empty state of its own: a week with nothing logged is seven
             NO DATA rows, which the design already draws and which says more
             than a single "nothing here" line would — it shows the shape of the
             gap. The footer still explains the scale the empty tracks are on. */
          <div className="milo-rows">
            {days.map((day) => (
              <div className="milo-row milo-day-row" key={day.iso}>
                <span className="milo-day-label">{day.label}</span>
                <div className="milo-bar">
                  {day.totals ? (
                    <div
                      className={[
                        'milo-bar-fill',
                        day.isToday ? 'milo-bar-fill--today' : 'milo-bar-fill--past',
                        /* A full bar is not an achievement — calories are a
                           CEILING. A day past it is hatched so the eye reads
                           "over", not "complete". Never fires against the
                           fallback axis; see `overTarget`. */
                        overTarget(day.totals.calories, scale) ? 'milo-bar-fill--over' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={{ width: barWidth(day.totals.calories, scale) }}
                    />
                  ) : null}
                </div>
                <span className="milo-kcal">{day.totals ? day.totals.calories : '—'}</span>
                <span className="milo-daymacros">
                  {day.totals
                    ? `P${day.totals.protein_g} C${day.totals.carbs_g} F${day.totals.fat_g}`
                    : 'NO DATA'}
                </span>
              </div>
            ))}
            <div className="milo-rows-foot">{scaleFooter(scale)}</div>
          </div>
        )}
      </Window>

      <Window title="WEEKLY-AVG.4W" shadow="m">
        {weekly.status === 'loading' ? (
          <div className="milo-note milo-note--sm">LOADING…</div>
        ) : weekly.status === 'error' ? (
          <div className="milo-note milo-note--sm">WEEKLY AVERAGES UNAVAILABLE</div>
        ) : (
          /* Always four rows, newest first — the server pads the window with
             empty weeks rather than dropping them, and an empty week is worth
             seeing: 0/7 DAYS is the one number that explains a missing bar. */
          <div className="milo-rows">
            {weekly.data.map((week) => (
              <div className="milo-row milo-week-row" key={week.week_start}>
                <span className="milo-day-label milo-week-label">
                  WK OF {monthLabel(parseIso(week.week_start))}
                </span>
                <div className="milo-bar">
                  {week.avg_calories === null ? null : (
                    /* The SAME axis as the panel above, deliberately: two tracks
                       of identical width drawn to different scales would make a
                       1,900 kcal day and a 1,900 kcal week average look like
                       different numbers. The footer says so. */
                    <div
                      className={[
                        'milo-bar-fill milo-bar-fill--week',
                        overTarget(week.avg_calories, scale) ? 'milo-bar-fill--over' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={{ width: barWidth(week.avg_calories, scale) }}
                    />
                  )}
                </div>
                <span className="milo-kcal">
                  {week.avg_calories === null ? '—' : Math.round(week.avg_calories)}
                </span>
                <span className="milo-week-chip">{week.days_with_data}/7 DAYS</span>
              </div>
            ))}
            <div className="milo-rows-foot">
              AVERAGE OF DAYS WITH DATA ONLY — SPARSE WEEKS READ ACCORDINGLY ✦ SAME
              KCAL SCALE AS ABOVE
            </div>
          </div>
        )}
      </Window>
    </>
  )
}

export default Macros
